import moment from 'moment';

export interface BankHoliday {
  date: string; // Format: YYYY-MM-DD
  name: string;
}

// UK Bank Holidays for 2024-2027
// Source: https://www.gov.uk/bank-holidays
// The API calculates these instead (backend/api/Classes/UkBankHolidays.cs);
// this list needs extending before 2028.
export const UK_BANK_HOLIDAYS: BankHoliday[] = [
  // 2024
  { date: '2024-01-01', name: "New Year's Day" },
  { date: '2024-03-29', name: 'Good Friday' },
  { date: '2024-04-01', name: 'Easter Monday' },
  { date: '2024-05-06', name: 'Early May Bank Holiday' },
  { date: '2024-05-27', name: 'Spring Bank Holiday' },
  { date: '2024-08-26', name: 'Summer Bank Holiday' },
  { date: '2024-12-25', name: 'Christmas Day' },
  { date: '2024-12-26', name: 'Boxing Day' },
  
  // 2025
  { date: '2025-01-01', name: "New Year's Day" },
  { date: '2025-04-18', name: 'Good Friday' },
  { date: '2025-04-21', name: 'Easter Monday' },
  { date: '2025-05-05', name: 'Early May Bank Holiday' },
  { date: '2025-05-26', name: 'Spring Bank Holiday' },
  { date: '2025-08-25', name: 'Summer Bank Holiday' },
  { date: '2025-12-25', name: 'Christmas Day' },
  { date: '2025-12-26', name: 'Boxing Day' },
  
  // 2026
  { date: '2026-01-01', name: "New Year's Day" },
  { date: '2026-04-03', name: 'Good Friday' },
  { date: '2026-04-06', name: 'Easter Monday' },
  { date: '2026-05-04', name: 'Early May Bank Holiday' },
  { date: '2026-05-25', name: 'Spring Bank Holiday' },
  { date: '2026-08-31', name: 'Summer Bank Holiday' },
  { date: '2026-12-25', name: 'Christmas Day' },
  { date: '2026-12-28', name: 'Boxing Day (substitute)' }, // Boxing Day on Saturday, so Monday is substitute holiday
  
  // 2027
  { date: '2027-01-01', name: "New Year's Day" },
  { date: '2027-03-26', name: 'Good Friday' },
  { date: '2027-03-29', name: 'Easter Monday' },
  { date: '2027-05-03', name: 'Early May Bank Holiday' },
  { date: '2027-05-31', name: 'Spring Bank Holiday' },
  { date: '2027-08-30', name: 'Summer Bank Holiday' },
  { date: '2027-12-27', name: 'Christmas Day (substitute)' }, // Christmas on Saturday
  { date: '2027-12-28', name: 'Boxing Day (substitute)' }, // Boxing Day on Sunday
];

/**
 * Check if a given date is a UK bank holiday
 * @param date - Date to check
 * @returns The bank holiday object if the date is a bank holiday, null otherwise
 */
export const isBankHoliday = (date: Date): BankHoliday | null => {
  const dateStr = moment(date).format('YYYY-MM-DD');
  const holiday = UK_BANK_HOLIDAYS.find(h => h.date === dateStr);
  return holiday || null;
};

/**
 * Get all bank holidays for a given year
 * @param year - The year to get bank holidays for
 * @returns Array of bank holidays for that year
 */
export const getBankHolidaysForYear = (year: number): BankHoliday[] => {
  return UK_BANK_HOLIDAYS.filter(h => h.date.startsWith(year.toString()));
};

/**
 * Get all bank holidays within a date range
 * @param startDate - Start of the date range
 * @param endDate - End of the date range
 * @returns Array of bank holidays within the range
 */
export const getBankHolidaysInRange = (startDate: Date, endDate: Date): BankHoliday[] => {
  const start = moment(startDate);
  const end = moment(endDate);
  
  return UK_BANK_HOLIDAYS.filter(h => {
    const holidayDate = moment(h.date);
    return holidayDate.isSameOrAfter(start, 'day') && holidayDate.isSameOrBefore(end, 'day');
  });
};
